import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import { useMemo, useState } from "react";

import { Button, Card, Dialog, TBody, TD, TH, THead, TR, Table, TextField } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import { browserPrinter } from "@/peripherals";
import { useAuthStore } from "@/stores/authStore";
import {
  localRefundRepository,
  needsManagerOffline,
  OfflineRefundRefused,
  type LocalRefund,
  type RefundableSale,
} from "@/repositories/localRefundRepository";

/**
 * Refunds while the till has no signal.
 *
 * Shown in place of Recent sales, which needs the server. Lists this till's own
 * cash sales from the last week — the only ones it may refund offline — and
 * says so, so a cashier is not left wondering why a card sale or another
 * counter's receipt is missing.
 */
export function OfflineRefunds({ tillId }: { tillId: string }) {
  const [open, setOpen] = useState<RefundableSale | null>(null);
  const [done, setDone] = useState<{ refund: LocalRefund; sale: RefundableSale } | null>(null);

  const sales = useQuery({
    queryKey: ["offline-refunds", "sales"],
    queryFn: () => localRefundRepository.refundableSales(),
  });
  const limit = useQuery({
    queryKey: ["offline-refunds", "limit"],
    queryFn: () => localRefundRepository.limit(),
  });

  const rows = sales.data?.slice(0, 8) ?? [];

  return (
    <Card flush className="p-5">
      <h2 className="text-base font-semibold text-ink-900">Refunds with no signal</h2>
      <p className="mt-1 text-sm text-ink-500">
        {limit.data
          ? `Cash sales from this till, up to ${formatMoney(limit.data.toFixed(2))} a refund. Anything else needs signal.`
          : "This shop does not give refunds without signal."}
      </p>

      {done && <RefundSlip refund={done.refund} sale={done.sale} onClose={() => setDone(null)} />}

      {limit.data ? (
        rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-500">
            No cash sales from this till in the last week.
          </p>
        ) : (
          <div className="mt-3">
            <Table>
              <THead>
                <TH>Receipt</TH>
                <TH className="text-right">Total</TH>
                <TH>Time</TH>
              </THead>
              <TBody>
                {rows.map((sale) => (
                  <TR key={sale.id}>
                    <TD>
                      <button
                        type="button"
                        onClick={() => {
                          setDone(null);
                          setOpen(sale);
                        }}
                        className="font-mono text-xs font-medium text-brand-600 hover:underline"
                      >
                        {sale.receiptNo}
                      </button>
                    </TD>
                    <TD className="text-right tabular-nums">{formatMoney(sale.total)}</TD>
                    <TD className="text-sm text-ink-500">
                      {new Date(sale.when).toLocaleString("en-ZA", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )
      ) : null}

      {open && (
        <OfflineRefundDialog
          sale={open}
          tillId={tillId}
          limit={limit.data ?? 0}
          onClose={() => setOpen(null)}
          onDone={(refund) => {
            setDone({ refund, sale: open });
            setOpen(null);
          }}
        />
      )}
    </Card>
  );
}

function OfflineRefundDialog({
  sale,
  tillId,
  limit,
  onClose,
  onDone,
}: {
  sale: RefundableSale;
  tillId: string;
  limit: number;
  onClose: () => void;
  onDone: (refund: LocalRefund) => void;
}) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<Record<number, { quantity: number; restock: boolean }>>({});
  const [reason, setReason] = useState("");
  const [approverCode, setApproverCode] = useState("");
  const [approverPin, setApproverPin] = useState("");
  // A cashier who may not refund alone gets a manager's approval here too,
  // checked against the manager's offline grant on this till.
  const needsManager = useQuery({
    queryKey: ["offline-refunds", "needs-manager"],
    queryFn: () => needsManagerOffline(),
  });

  const lines = sale.lines.filter((line) => Number(line.remaining) > 0);
  const total = useMemo(
    () =>
      lines.reduce(
        (sum, line) => sum + Number(line.unitPrice) * (chosen[line.lineNumber]?.quantity ?? 0),
        0,
      ),
    [chosen, lines],
  );

  const refund = useMutation({
    mutationFn: () =>
      localRefundRepository.refund({
        sale,
        tillId,
        reason,
        ...(needsManager.data
          ? { approver: { staffCode: approverCode.trim().toUpperCase(), pin: approverPin } }
          : {}),
        lines: Object.entries(chosen)
          .filter(([, value]) => value.quantity > 0)
          .map(([lineNumber, value]) => ({ lineNumber: Number(lineNumber), ...value })),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["offline-refunds"] });
      onDone(result);
    },
    // A refused PIN must not stay in the field for the next person to see.
    onError: () => setApproverPin(""),
  });

  const over = total > limit;
  const valid =
    total > 0 &&
    !over &&
    reason.trim().length > 0 &&
    (!needsManager.data || (approverCode.trim().length > 0 && approverPin.length >= 4));

  function setQuantity(lineNumber: number, quantity: number, max: number) {
    const capped = Math.max(0, Math.min(quantity || 0, max));
    setChosen((current) => ({
      ...current,
      [lineNumber]: { quantity: capped, restock: current[lineNumber]?.restock ?? true },
    }));
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Refund ${sale.receiptNo}`}
      description="No signal: cash back from this drawer. It uploads when the signal returns."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={refund.isPending}
            disabled={!valid}
            onClick={() => refund.mutate()}
          >
            Refund {formatMoney(total.toFixed(2))} cash
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="divide-y divide-line">
          {lines.map((line) => {
            const value = chosen[line.lineNumber];
            return (
              <li key={line.lineNumber} className="flex flex-wrap items-center gap-3 py-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-ink-900">{line.description}</span>
                  <span className="block text-xs text-ink-500">
                    {formatMoney(line.unitPrice)} each · {line.remaining} left to give back
                  </span>
                </span>
                <label className="flex items-center gap-2">
                  <span className="sr-only">How many of {line.description}</span>
                  <input
                    type="number"
                    min="0"
                    max={line.remaining}
                    step="1"
                    inputMode="numeric"
                    value={value?.quantity || ""}
                    onChange={(event) =>
                      setQuantity(
                        line.lineNumber,
                        Number(event.target.value),
                        Number(line.remaining),
                      )
                    }
                    className="h-9 w-20 rounded-control border border-line bg-surface px-2 text-sm tabular-nums text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setQuantity(line.lineNumber, Number(line.remaining), Number(line.remaining))
                    }
                  >
                    All
                  </Button>
                </label>
                {(value?.quantity ?? 0) > 0 && (
                  <label
                    className={cn(
                      "flex items-center gap-2 rounded-control border px-2.5 py-1.5 text-xs",
                      value?.restock !== false
                        ? "border-line text-ink-600"
                        : "border-warn-500/30 bg-warn-50 text-warn-700",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={value?.restock !== false}
                      onChange={(event) =>
                        setChosen((current) => ({
                          ...current,
                          [line.lineNumber]: {
                            quantity: current[line.lineNumber]?.quantity ?? 0,
                            restock: event.target.checked,
                          },
                        }))
                      }
                      className="size-3.5 rounded border-line text-brand-500 focus:ring-brand-500/20"
                    />
                    {value?.restock !== false ? "Back on the shelf" : "Written off"}
                  </label>
                )}
              </li>
            );
          })}
        </ul>

        <TextField
          label="Why is it coming back?"
          value={reason}
          maxLength={300}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Customer said it was flat"
          hint="Recorded against the refund, with your name."
        />

        {needsManager.data && (
          <div className="grid gap-3 rounded-control bg-warn-50 p-3 sm:grid-cols-2">
            <p className="text-sm text-warn-700 sm:col-span-2">
              A manager approves this refund. They enter their own staff code and PIN here. It works
              with no signal for a manager who has signed in on this till with signal recently.
            </p>
            <TextField
              label="Manager's staff code"
              value={approverCode}
              autoComplete="off"
              onChange={(event) => setApproverCode(event.target.value.toUpperCase())}
            />
            <TextField
              label="Manager's PIN"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              value={approverPin}
              onChange={(event) => setApproverPin(event.target.value.replace(/\D/g, ""))}
            />
          </div>
        )}

        {over && (
          <p className="text-sm text-warn-700">
            Over this shop&apos;s limit of {formatMoney(limit.toFixed(2))} for a refund with no
            signal. Give back less, or wait for signal.
          </p>
        )}
        {refund.error != null && (
          <p className="text-sm text-danger-700">
            {refund.error instanceof OfflineRefundRefused
              ? refund.error.message
              : "That refund could not be saved on this till."}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/**
 * The slip for a refund given with no signal.
 *
 * Printed the way a sale's receipt is — the browser prints whatever sits inside
 * `data-print="receipt"` — so the customer leaves with paper and the drawer's
 * cash-up has something to match. It says plainly that the refund is still only
 * on this till, because until it uploads that is the truth.
 */
function RefundSlip({
  refund,
  sale,
  onClose,
}: {
  refund: LocalRefund;
  sale: RefundableSale;
  onClose: () => void;
}) {
  const cashier = useAuthStore((s) => s.session?.user.displayName ?? "");
  const byNumber = new Map(sale.lines.map((line) => [line.lineNumber, line]));
  const lines = refund.lines.map((line) => {
    const original = byNumber.get(line.lineNumber);
    const quantity = Number(line.quantity);
    return {
      lineNumber: line.lineNumber,
      description: original?.description ?? `Line ${line.lineNumber}`,
      quantity,
      total: (Number(original?.unitPrice ?? 0) * quantity).toFixed(2),
      restock: line.restock,
    };
  });

  const payload = {
    receiptNumber: `Refund of ${refund.receiptNo}`,
    merchantName: "",
    lines: lines.map((line) => ({
      description: line.description,
      quantity: String(-line.quantity),
      unitPrice: (Number(line.total) / line.quantity).toFixed(2),
      lineTotal: `-${line.total}`,
    })),
    subtotal: `-${refund.total}`,
    taxTotal: "0.00",
    total: `-${refund.total}`,
    method: "CASH",
    cashier,
    when: refund.createdAt,
    queued: true,
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="Refund given"
      description={`Refund of ${refund.receiptNo}`}
      footer={
        <>
          <Button variant="secondary" onClick={() => void browserPrinter.print(payload)}>
            <Printer className="size-4" aria-hidden /> Print
          </Button>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      <div className="space-y-4" data-print="receipt">
        <p className="text-center text-sm font-semibold uppercase tracking-wide text-ink-900">
          Refund · {refund.receiptNo}
        </p>

        <div className="rounded-card bg-brand-50 px-4 py-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
            Cash to give back
          </p>
          <p className="text-total tabular-nums text-brand-600">{formatMoney(refund.total)}</p>
        </div>

        <ul className="divide-y divide-line text-sm">
          {lines.map((line) => (
            <li key={line.lineNumber} className="flex items-baseline justify-between gap-3 py-2">
              <span className="min-w-0">
                <span className="tabular-nums text-ink-500">{line.quantity}x </span>
                <span className="text-ink-900">{line.description}</span>
                {!line.restock && <span className="text-ink-500"> (written off)</span>}
              </span>
              <span className="tabular-nums text-ink-900">-{formatMoney(line.total)}</span>
            </li>
          ))}
        </ul>

        <p className="text-sm text-ink-600">Reason: {refund.reason}</p>

        <p className="rounded-card border border-warn-500/30 bg-warn-50 px-4 py-3 text-sm text-warn-700">
          Given with no signal. Saved on this till and uploads when the connection returns.
        </p>

        <p className="text-xs text-ink-500">
          {cashier} ·{" "}
          {new Date(refund.createdAt).toLocaleString("en-ZA", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </Dialog>
  );
}
